import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useBackTo } from '../../app/nav/originNav'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import Badge, { type BadgeTone } from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'
import AttachmentList from '../../components/ui/AttachmentList'
import LoadingSkeleton from '../../components/ui/LoadingSkeleton'
import ScoreGauge, { type GaugeTone } from './components/ScoreGauge'
import CriterionScoreBars from './components/CriterionScoreBars'
import { useExecutiveSummary } from './hooks/useExecutiveSummary'
import { useUpdateParticipation, PARTICIPATION_STATUS } from './hooks/useUpdateParticipation'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { useToast } from '../../components/ui/ToastProvider'
import { formatDate } from '../../lib/datetime'
import type { PriorityLevel } from './types'
import './activity.css'

// NOT: FR-017 (Personel yalnız kendi etkinliğini görebilmeli) ve FR-018 (Personel katılım durumunu
// görmemeli) kodda henüz uygulanmıyor — bkz. unitOpinions[].participationStatus'ün koşulsuz render'ı
// ve ActivityController'daki tek `ACTIVITY_VIEW` grant'ı (own-scope filtresi yok). Bugün Personel
// rolünün DB'de aktif grant taşımaması bunu maskeliyor; analist grant verirse ihlal açığa çıkar
// (029 Clarifications 2026-08-18). Stratejik skor/öncelik GM kararı değildir, o kısım gösterilebilir.

const LEVEL_TONE: Record<PriorityLevel, GaugeTone> = { LOW: 'low', MEDIUM: 'mid', HIGH: 'high' }
const LEVEL_BADGE: Record<PriorityLevel, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'blue',
  HIGH: 'green',
}

export default function ExecutiveSummaryPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  // "Geri": origin varsa (geldiği liste) oraya, yoksa Etkinlik Listesi'ne. useBackTo erken return'lerden ÖNCE.
  const backTo = useBackTo('/events/list')
  const [params] = useSearchParams()
  const toast = useToast()
  const participation = useUpdateParticipation()
  // Katıl/Katılma karar kartı yalnız katılım durumu değiştirme yetkisi olan kullanıcıda (FR-016; asıl koruma backend'de).
  const { hasGrant } = useGrants()

  const id = useMemo(() => {
    const raw = params.get('id')
    if (raw == null || raw.trim() === '') return null
    const n = Number(raw)
    return Number.isInteger(n) && n > 0 ? n : null
  }, [params])

  const { data, isLoading, isError } = useExecutiveSummary(id)

  // id yoksa (doğrudan menü): listeden seçim iste
  if (id == null) {
    return (
      <div className="ui-page">
        <EmptyState
          icon="pi pi-list"
          title={t('activity.summary.noSelection.title')}
          description={t('activity.summary.noSelection.desc')}
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="ui-page">
        <LoadingSkeleton lines={6} />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="ui-page">
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('activity.summary.notFound.title')}
          description={t('activity.summary.notFound.desc')}
        />
      </div>
    )
  }

  const s = data
  const level = s.priority?.level
  const tone: GaugeTone = level ? LEVEL_TONE[level] : 'low'
  const place = [s.city, s.country].filter(Boolean).join(' · ')

  const chips: { label?: string; value: string }[] = []
  if (place) chips.push({ label: t('activity.summary.chips.place'), value: place })
  chips.push({
    label: t('activity.summary.chips.date'),
    value: `${formatDate(s.startDate)} – ${formatDate(s.endDate)}`,
  })
  if (s.eventType) chips.push({ label: t('activity.summary.chips.type'), value: s.eventType })
  if (s.invitationType)
    chips.push({ label: t('activity.summary.chips.invitation'), value: s.invitationType })
  if (s.numberOfParticipants != null)
    chips.push({
      label: t('activity.summary.chips.participants'),
      value: s.numberOfParticipants.toLocaleString(i18n.language),
    })

  const subParts = [
    `${t('activity.summary.organizer')}: ${s.organizingInstitution}`,
    `${t('activity.summary.requestNo')}: ${s.id}`,
  ]
  if (s.requesterUnit) subParts.push(`${t('activity.summary.requester')}: ${s.requesterUnit}`)

  // Katıl/Katılma → katılım durumu (GM kararı) güncelle; başarıda özet/liste tazelenir (hook).
  const decide = (statusId: number) =>
    participation.mutate(
      { id: id!, statusId }, // id guard'lardan sonra kesin non-null; TS closure sinirini asamadigi icin assert

      {
        onSuccess: () =>
          toast.show({ severity: 'success', summary: t('activity.actions.updated'), life: 3000 }),
        onError: () =>
          toast.show({ severity: 'error', summary: t('common.errors.generic'), life: 3000 }),
      },
    )

  return (
    <div className="ui-page">
      <div className="exec-actions">
        <Button
          label={t('activity.summary.back')}
          variant="secondary"
          icon="pi pi-arrow-left"
          onClick={() => navigate(backTo)}
        />
        <Button
          label={t('activity.summary.print')}
          outlined
          icon="pi pi-print"
          onClick={() => window.print()}
        />
      </div>

      {/* Hero */}
      <div className="exec-hero">
        <div className="exec-hero-main">
          <div className="exec-eyebrow">{t('activity.summary.eyebrow')}</div>
          <h1 className="exec-title">{s.name}</h1>
          <div className="exec-sub">{subParts.join(' · ')}</div>
          <div className="exec-chips">
            {chips.map((c, i) => (
              <span className="exec-chip" key={i}>
                {c.label ? `${c.label}: ` : ''}
                <b>{c.value}</b>
              </span>
            ))}
          </div>
        </div>
        <div className="exec-score">
          <ScoreGauge score={s.strategicScore} tone={tone} />
          {s.priority && level && (
            <Badge tone={LEVEL_BADGE[level]} variant="dot">
              {s.priority.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Katılım kararı — belirgin aksiyon kartı (bilet Onayla/Reddet kalıbı: yeşil Katıl / kırmızı Katılma).
          Yalnız: (a) katılım durumu değiştirme yetkisi varsa VE (b) etkinlik işlem görmemişse (IS_PROCESSED=10). */}
      {hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_UPDATE) && !s.isProcessed && (
        <div className="exec-decision">
          <div className="exec-decision-info">
            <h3 className="exec-decision-title">{t('activity.summary.decisionTitle')}</h3>
            <p className="exec-decision-desc">{t('activity.summary.decisionDesc')}</p>
          </div>
          <div className="exec-decision-acts">
            <Button
              label={t('activity.actions.attend')}
              icon="pi pi-check"
              severity="success"
              disabled={participation.isPending}
              onClick={() => decide(PARTICIPATION_STATUS.ATTENDING)}
            />
            <Button
              label={t('activity.actions.decline')}
              icon="pi pi-times"
              severity="danger"
              outlined
              disabled={participation.isPending}
              onClick={() => decide(PARTICIPATION_STATUS.NOT_ATTENDING)}
            />
          </div>
        </div>
      )}

      {/* Öneri/verdict bandı ("GM katılımı önerilir" / "Delege / alternatif değerlendirilebilir" /
          "Katılım önerilmez") 2026-07-31'de kullanıcı isteğiyle KALDIRILDI. Öncelik bilgisi zaten hero'daki
          skor göstergesi + öncelik rozetiyle veriliyor; ayrıca bir öneri kartı gösterilmiyor. Uçtan gelen
          `verdictDescription` alanı bu ekranda artık kullanılmıyor (sözleşme değişmedi). */}

      <div className="exec-row2">
        {/* Kriter kırılımı — yalnız skorlu etkinlikte */}
        {s.criteria.length > 0 && (
          <div className="ev-card">
            <div className="ev-score-head">
              <h3 className="ev-score-title">{t('activity.summary.criteria.title')}</h3>
            </div>
            <CriterionScoreBars criteria={s.criteria} />
          </div>
        )}

        <div className="exec-stack">
          {/* Katılımcı Profili (paydaşlar) — boşsa gizli */}
          {s.stakeholders.length > 0 && (
            <div className="ev-card">
              <div className="ev-score-head">
                <h3 className="ev-score-title">{t('activity.summary.stakeholders.title')}</h3>
              </div>
              <div className="exec-chip-list">
                {s.stakeholders.map((name, i) => (
                  <span className="exec-chip" key={i}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Öne Çıkan Katılımcılar — boşsa gizli */}
          {s.featuredParticipants && (
            <div className="ev-card">
              <div className="ev-score-head">
                <h3 className="ev-score-title">{t('activity.summary.featured.title')}</h3>
              </div>
              <div className="meta-list">
                <div className="mi">
                  <span className="vv" style={{ textAlign: 'left', fontWeight: 400 }}>
                    {s.featuredParticipants}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Birim Görüşleri — boşsa gizli */}
      {s.unitOpinions.length > 0 && (
        <div className="ev-card" style={{ marginTop: 16 }}>
          <div className="ev-score-head">
            <h3 className="ev-score-title">{t('activity.summary.units.title')}</h3>
          </div>
          <table className="ev-score-table">
            <thead>
              <tr>
                <th>{t('activity.summary.units.unit')}</th>
                <th>{t('activity.summary.units.status')}</th>
                <th>{t('activity.summary.units.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {s.unitOpinions.map((u, i) => (
                <tr key={i}>
                  <td>{u.unitName}</td>
                  <td>{u.participationStatus}</td>
                  <td>{u.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dosya Ekleri — ortak AttachmentList (Reuse Mandate); boşsa gizli. İndirme ortak uçtan. */}
      {s.attachments.length > 0 && (
        <div className="ev-card" style={{ marginTop: 16 }}>
          <AttachmentList
            title={t('activity.summary.attachments.title')}
            items={s.attachments}
            /* Bağlam: ATTACHED_FILE.TABLE_NAME = 'ACTIVITY' → etkinlik okuma yetkisi. */
            requiredGrant={GRANTS.ACTIVITY_VIEW}
          />
        </div>
      )}
    </div>
  )
}
